import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DocumentTagAndId } from '@app/features/documents/document.model';
import { RUNTIME_CONFIG } from '@environments/runtime-config.token';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  Amendment,
  AmendmentEligibility,
  AmendmentPayload,
  AmendmentReasons,
  WorkingConditions,
} from './amendment.model';

export interface IAmendmentService {
  updated$: Observable<Amendment>;
  listOfContract(contractId: string): Observable<Amendment[]>;
  findOne(amendmentId: string): Observable<Amendment>;
}

/** Les avenants d'un contrat en cours (cadrage 2026-014). */
@Injectable({ providedIn: 'root' })
export class AmendmentService implements IAmendmentService {
  private http = inject(HttpClient);
  private apiUrl = inject(RUNTIME_CONFIG).apiUrl;
  private updatedSubject = new Subject<Amendment>();

  /** Chaque avenant relu après un geste : liste et dialogue se mettent à jour. */
  get updated$(): Observable<Amendment> {
    return this.updatedSubject.asObservable();
  }

  listOfContract(contractId: string): Observable<Amendment[]> {
    return this.http.get<Amendment[]>(`${this.apiUrl}/contracts/${contractId}/amendments`);
  }

  eligibility(contractId: string): Observable<AmendmentEligibility> {
    return this.http.get<AmendmentEligibility>(
      `${this.apiUrl}/contracts/${contractId}/amendment-eligibility`,
    );
  }

  /** Les conditions en vigueur à une date : de quoi préremplir un avenant. */
  workingConditions(contractId: string, at: string): Observable<WorkingConditions> {
    return this.http.get<WorkingConditions>(
      `${this.apiUrl}/contracts/${contractId}/working-conditions`,
      { params: { at } },
    );
  }

  /** Les motifs de conditions en cours de saisie, sans rien enregistrer. */
  reasons(
    contractId: string,
    payload: AmendmentPayload,
    amendmentId?: string,
  ): Observable<AmendmentReasons> {
    const params: Record<string, string> = amendmentId ? { amendmentId } : {};
    return this.http.post<AmendmentReasons>(
      `${this.apiUrl}/contracts/${contractId}/amendments/reasons`,
      payload,
      { params },
    );
  }

  establish(contractId: string, payload: AmendmentPayload): Observable<Amendment> {
    return this.http
      .post<Amendment>(`${this.apiUrl}/contracts/${contractId}/amendments`, payload)
      .pipe(tap((amendment) => this.updatedSubject.next(amendment)));
  }

  findOne(amendmentId: string): Observable<Amendment> {
    return this.http
      .get<Amendment>(`${this.apiUrl}/amendments/${amendmentId}`)
      .pipe(tap((amendment) => this.updatedSubject.next(amendment)));
  }

  update(amendmentId: string, payload: AmendmentPayload): Observable<Amendment> {
    return this.http
      .put<Amendment>(`${this.apiUrl}/amendments/${amendmentId}`, payload)
      .pipe(tap((amendment) => this.updatedSubject.next(amendment)));
  }

  /** Un brouillon s'abandonne sans motif ; un avenant en signature, avec le sien. */
  abandon(amendmentId: string, comment: string | null = null): Observable<Amendment> {
    return this.http
      .post<Amendment>(`${this.apiUrl}/amendments/${amendmentId}/abandon`, { comment })
      .pipe(tap((amendment) => this.updatedSubject.next(amendment)));
  }

  /** Le document de l'avenant, tamponné « brouillon » avant sa mise en signature. */
  pdf(amendmentId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/amendments/${amendmentId}/pdf`, { responseType: 'blob' });
  }

  requestSignature(amendmentId: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/amendments/${amendmentId}/signature-request`, {});
  }

  /** Dépose l'exemplaire signé d'un avenant signé hors de la signature électronique. */
  addSignedDocument(amendmentId: string, tagAndId: DocumentTagAndId): Observable<Amendment> {
    return this.http
      .put<Amendment>(`${this.apiUrl}/amendments/${amendmentId}/signed-document`, tagAndId)
      .pipe(tap((amendment) => this.updatedSubject.next(amendment)));
  }
}
